import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { UserModel } from '../../models/user.model';
import { UtilsService } from '../utils.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NoauthService } from '../noauth.service';
import { RBACService } from '../rbac.service';
import { AppService } from '../../app.service';

@Component({
  selector: 'app-user-public-profile',
  templateUrl: './user-public-profile.component.html',
  styleUrls: ['./user-public-profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class UserPublicProfileComponent implements OnInit {
  user = new UserModel();
  shareLink: string;
  readonly baseUrl = window.location.origin;

  // RBAC permission flags for tab visibility
  canViewForms: boolean = false;
  canViewSitesVisited: boolean = false;
  canViewSitesWorkedOn: boolean = false;
  canEditSitesWorkedOn: boolean = false;
  canDeleteSitesWorkedOn: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public hash: string,
    private dialog: MatDialogRef<UserPublicProfileComponent>,
    private noauthService: NoauthService,
    public utils: UtilsService,
    public rbac: RBACService,
    public app: AppService
  ) {}

  ngOnInit() {
    // Tab visibility. Each of these three tabs shows ACCOUNT SCOPED data, unlike the Details and
    // Training/Competencies tabs, which show the public profile the worker chose to publish.
    //
    // Each flag needs both gates: the account's plan must include the feature (resolved through the
    // authoritative RBAC feature to plan feature map, so Forms and Sites do not have to be named
    // here), and the user must hold the feature's view permission with exact: true.
    //
    // These previously ORed CORE_PERMISSION_USER_PROFILE into each list with the default exact: false.
    // Every authenticated user holds the core personal permission, which is exactly why
    // holdsNoFeaturePermissions() filters it out when counting real permissions, so all three flags
    // were true for everyone. That is what QA saw on Free accounts: not a plan gating gap, but three
    // tabs that were effectively ungated on every plan, including for a user with no Forms or Sites
    // permission at all on Premium.
    this.canViewForms =
      this.app.planCanRbacFeature(RBACService.RBAC_F.F_FORMS) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_FORMS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );

    this.canViewSitesVisited =
      this.app.planCanRbacFeature(
        RBACService.RBAC_F.F_EMPLOYEES_SITE_ASSOCIATIONS
      ) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES_SITE_ASSOCIATIONS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );

    // Sites Worked On is reachable from either perspective, so either view permission qualifies.
    // Both slugs map to the Sites plan feature, so one plan check covers them.
    this.canViewSitesWorkedOn =
      this.app.planCanRbacFeature(RBACService.RBAC_F.F_SITES_USERS_WORKED_ON) &&
      this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES_SITE_ASSOCIATIONS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_USERS_WORKED_ON,
          RBACService.RBAC_A.A_VIEW
        )
      ]);

    this.canEditSitesWorkedOn = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_EMPLOYEES_SITE_ASSOCIATIONS,
        RBACService.RBAC_A.A_EDIT
      ),
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES_USERS_WORKED_ON,
        RBACService.RBAC_A.A_EDIT
      )
    ]);

    this.canDeleteSitesWorkedOn = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_EMPLOYEES_SITE_ASSOCIATIONS,
        RBACService.RBAC_A.A_DELETE
      ),
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES_USERS_WORKED_ON,
        RBACService.RBAC_A.A_DELETE
      )
    ]);

    this.find();
  }

  find() {
    this.noauthService.findUser(this.hash, false).subscribe(
      (response) => {
        this.user = response.data;
        this.shareLink =
          this.baseUrl + `/noauth/user-public-profile/${this.hash}`;
      },
      (error) => {
        this.utils.showModal(
          'Error Message',
          'Sorry, we could not retrieve the user’s profile. Please try again later.'
        );
        this.dialog.close();
      }
    );
  }

  notifyCopied() {
    this.utils.showToast('Copied link to clipboard');
  }

  onOpenUrl(url: string) {
    this.utils.openUrl(url);
  }

  protected readonly RBACService = RBACService;
}
